import React, { useCallback } from 'react';
import { Checkbox } from 'antd';
import { styled } from 'styled-components';
import { Column, Label, RowCentered, Text } from '@ydistri/ds';
import { AsoScenarioAdditionalOneStepAdjustment, AsoScenarioUseSets } from '@ydistri/api-sdk';
import { useAsoConfigurations } from '../useAsoConfigurations';
import AsoScenarioUseSetsSelector from '../components/AsoScenarioUseSetsSelector';
import AsoScenarioAdditionalOneStepAdjustmentSelector from '../components/AsoScenarioAdditionalOneStepAdjustmentSelector';
import {
  InputNumberFullWidth,
  LabelFieldWrapper,
  TextWithTooltip,
} from '../components/asoSettingsStyledComponents';
import { AsoNumericFieldRow } from '../components/AsoNumericFieldRow';
import AsoTooltip from '../../AsoTooltip';
import { AsoValidationIssueMessages } from '../../validation/AsoValidationIssueMessages';
import {
  getAsoValidationIssueStatus,
  getAsoValidationIssuesForField,
} from '../../validation/asoValidation';
import { useAsoValidation } from '../../validation/useAsoValidation';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 16rem minmax(9rem, 12rem) minmax(9rem, 12rem);
  gap: 0.5rem 0.25rem;
  align-items: start;
`;

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

const FeedbackRow = styled.div`
  display: grid;
  grid-template-columns: 16rem max-content;
  gap: 0.125rem 0.25rem;
  align-items: center;
`;

const FeedbackMessages = styled.div`
  grid-column: 1 / -1;
`;

const RedistributionAndSets: React.FC = () => {
  const { config, handleChange, debouncedHandleChange } = useAsoConfigurations();
  const { issuesByArea } = useAsoValidation();
  const settingsIssues = issuesByArea.settings;

  const getFieldIssues = useCallback(
    (field: string) => getAsoValidationIssuesForField(settingsIssues, field),
    [settingsIssues],
  );

  const getFieldStatus = useCallback(
    (field: string) => getAsoValidationIssueStatus(getFieldIssues(field)),
    [getFieldIssues],
  );

  const showSupplyModifiers =
    config?.useSets === AsoScenarioUseSets.HumanFeasibleMinSets ||
    config?.useSets === AsoScenarioUseSets.Both;
  const showMaxPickingPositionsForOneSku =
    config?.additionalOneStepAdjustment ===
    AsoScenarioAdditionalOneStepAdjustment.MaxPickingPositionsForOneSku;

  if (!config) return null;

  const supply1Mirror =
    typeof config.supplyPreferenceModifier1Supply === 'number'
      ? config.supplyPreferenceModifier1Supply
      : undefined;
  const additionalOneStepAdjustmentIssues = getFieldIssues('additionalOneStepAdjustment');

  return (
    <Column $gap="0.5rem">
      <Label>Redistribution Quantity and Sets</Label>
      <RowCentered $gap="1rem">
        <Checkbox
          id="aso-useMinRedistributionQuantity"
          checked={config.useMinRedistributionQuantity}
          onChange={e => handleChange({ useMinRedistributionQuantity: e.target.checked })}
        />
        <TextWithTooltip as="label" htmlFor="aso-useMinRedistributionQuantity" $pointer>
          Use Min. Redistribution Quantity from data
          <AsoTooltip tooltipKey="useMinRedistributionQuantity" />
        </TextWithTooltip>
      </RowCentered>
      <RowCentered $gap="1rem">
        <Checkbox
          id="aso-ignoreMinRedistributionQtyAndSetWhenDepletingToZero"
          checked={config.ignoreMinRedistributionQtyAndSetWhenDepletingToZero}
          onChange={e =>
            handleChange({ ignoreMinRedistributionQtyAndSetWhenDepletingToZero: e.target.checked })
          }
        />
        <TextWithTooltip
          as="label"
          htmlFor="aso-ignoreMinRedistributionQtyAndSetWhenDepletingToZero"
          $pointer
        >
          Ignore Min. Redistribution Qty and Set when depleting to zero
          <AsoTooltip tooltipKey="ignoreMinRedistributionQtyAndSetWhenDepletingToZero" />
        </TextWithTooltip>
      </RowCentered>
      <LabelFieldWrapper>
        <TextWithTooltip $width="16rem">
          Use Sets <AsoTooltip tooltipKey="useSets" />
        </TextWithTooltip>

        <AsoScenarioUseSetsSelector
          value={config.useSets}
          onChange={v => handleChange({ useSets: v })}
        />
      </LabelFieldWrapper>

      {showSupplyModifiers && (
        <>
          <RowCentered $gap="0.25rem">
            <TextWithTooltip $width="16rem">
              Supply preference modifier
              <AsoTooltip tooltipKey="supplyPreferenceModifier" />
            </TextWithTooltip>
          </RowCentered>
          <Grid>
            {/* Row 1 */}
            <Text $width="16rem" $right>
              AS &lt;
            </Text>
            <InputNumberFullWidth disabled value={supply1Mirror} />
            <InputNumberFullWidth disabled value={1} />

            {/* Row 2 */}
            <Text $width="16rem" $right>
              &gt;=
            </Text>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier1Supply ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier1Supply')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier1Supply: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier1Supply')}
              />
            </FieldStack>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier1Modifier ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier1Modifier')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier1Modifier: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier1Modifier')}
              />
            </FieldStack>

            {/* Row 3 */}
            <Text $width="16rem" $right>
              &gt;=
            </Text>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier2Supply ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier2Supply')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier2Supply: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier2Supply')}
              />
            </FieldStack>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier2Modifier ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier2Modifier')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier2Modifier: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier2Modifier')}
              />
            </FieldStack>

            {/* Row 4 */}
            <Text $width="16rem" $right>
              &gt;=
            </Text>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier3Supply ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier3Supply')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier3Supply: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier3Supply')}
              />
            </FieldStack>
            <FieldStack>
              <InputNumberFullWidth
                value={config.supplyPreferenceModifier3Modifier ?? undefined}
                min={1}
                precision={0}
                status={getFieldStatus('supplyPreferenceModifier3Modifier')}
                onChange={v =>
                  debouncedHandleChange({
                    supplyPreferenceModifier3Modifier: typeof v === 'number' ? v : null,
                  })
                }
              />
              <AsoValidationIssueMessages
                issues={getFieldIssues('supplyPreferenceModifier3Modifier')}
              />
            </FieldStack>
          </Grid>
        </>
      )}
      <FeedbackRow>
        <TextWithTooltip $width="16rem">
          Additional one step adjustment
          <AsoTooltip tooltipKey="additionalOneStepAdjustment" />
        </TextWithTooltip>
        <AsoScenarioAdditionalOneStepAdjustmentSelector
          value={config.additionalOneStepAdjustment}
          onChange={v => handleChange({ additionalOneStepAdjustment: v })}
        />
        {additionalOneStepAdjustmentIssues.length > 0 && (
          <FeedbackMessages>
            <AsoValidationIssueMessages issues={additionalOneStepAdjustmentIssues} />
          </FeedbackMessages>
        )}
      </FeedbackRow>
      {showMaxPickingPositionsForOneSku && (
        <AsoNumericFieldRow
          label="Max Picking Positions for SKU"
          labelWidth="16rem"
          value={config.maxPickingPositionsForOneSku ?? undefined}
          min={1}
          precision={0}
          status={getFieldStatus('maxPickingPositionsForOneSku')}
          feedback={
            <AsoValidationIssueMessages issues={getFieldIssues('maxPickingPositionsForOneSku')} />
          }
          tooltipKey="maxPickingPositionsForOneSku"
          onChange={v =>
            debouncedHandleChange({
              maxPickingPositionsForOneSku: typeof v === 'number' ? v : null,
            })
          }
        />
      )}
    </Column>
  );
};

export default RedistributionAndSets;
